import { ChangeEvent, ReactElement, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ApplicationLocationContext } from "../../utils/ApplicationState";
import { config, pageNames } from "../../authConfig";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import styles from "./styles/Events.module.css";
import { Box, Button, Checkbox, darken, FormControlLabel, Menu, MenuItem, Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import { Link, useNavigate } from "react-router-dom";
import useFetchMsal from "../../Common/UI/hooks/useFetchMsal";
import SNEvent from "./interfaces/SNEvent";
import { GridLoadingOverlay } from "../../Common/UI/components/GridLoadingOverlay";
import dayjs from "dayjs";
import DownloadIcon from "@mui/icons-material/Download";
import { useSpiritNightTransition } from "../../home/ui-components/SpiritNightTransition";
import useIsAdminAndViewOnly from "../../Common/UI/hooks/useIsAdminAndViewOnly";

export const Events = (): ReactElement => {
  const { isViewOnly } = useIsAdminAndViewOnly();
  const { canCreateEvents } = useSpiritNightTransition();
  const { setState } = useContext(ApplicationLocationContext);
  const gridRef = useRef<AgGridReact>(null);
  const execute = useCallback(useFetchMsal(), []);
  const abortController = useRef<AbortController | null>(null);
  const navigate = useNavigate();
  const [events, setEvents] = useState<SNEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [oldEvents, setOldEvents] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [menuAnchorElement, setMenuAnchorElement] = useState<HTMLElement | null>(null);
  const [colsDef, setColsDef] = useState<ColDef[]>([
    {
      field: "id",
      headerName: "Event ID",
      cellRenderer: ({ value }: ICellRendererParams) => (
        <Link to={`/events/${value}`} state={events.find((x) => x.id === value)}>
          {value}
        </Link>
      ),
    },
    { field: "locationName", headerName: "Store Location Name" },
    { field: "vendor.companyName", headerName: "Vendor Name" },
    { field: "date", headerName: "Event Date" },
    { field: "state", headerName: "Event Status" },
  ]);

  useEffect(() => {
    setState({ path: [pageNames.events] });
  }, [setState]);

  useEffect(() => {
    const fetchEvents = async () => {
      abortController.current?.abort();
      abortController.current = new AbortController();

      setIsLoading(true);

      try {
        const url = `${config.rootAPIUrl}${config.rootAPIList?.events.replace("{oldEvents}", oldEvents.toString()).replace("{archived}", showArchived.toString())}`;
        const response = await execute("GET", url, undefined, abortController.current.signal);

        if (Array.isArray(response)) {
          response.forEach((item) => {
            if (item.date) {
              item.date = dayjs(item.date).format("YYYY-MM-DD");
            }

            if (item.state) {
              item.state = item.state.replace(/([a-z])([A-Z])/g, "$1 $2"); // Add space between camel-case words
            }
          });
        }

        setEvents(response);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();

    return () => abortController.current?.abort();
  }, [execute, oldEvents, showArchived]);

  const onInitialDataRender = async () => {
    await gridRef.current?.api.setColumnFilterModel("state", {
      operator: "AND",
      conditions: [
        { type: "notEqual", filter: "Closed" },
        { type: "notEqual", filter: "Cancelled" },
      ],
    });

    gridRef.current?.api.onFilterChanged();

    gridRef.current?.api.applyColumnState({
      state: [{ colId: "id", sort: "desc" }],
      defaultState: { sort: null },
    });
  };

  const handleExportAsCsvButtonClick = () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: `export_${dayjs(dayjs()).format("YYYY_MM_DD_HH_mm_ss").toString()}`,
    });
  };

  const handleColumnsMenuChange = (e: ChangeEvent<HTMLInputElement>) => {
    setColsDef((prev) => prev.map((col: ColDef) => (col.field === e.target.name ? { ...col, hide: !col.hide } : col)));
  };

  return (
    <Box sx={{ mx: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 1 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ViewWeekIcon />}
            sx={{
              alignSelf: "flex-end",
              mb: 1,
            }}
            disableElevation
            onClick={(e) => setMenuAnchorElement(e.currentTarget)}
          >
            Columns
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{
              alignSelf: "flex-end",
              mb: 1,
            }}
            disableElevation
            onClick={handleExportAsCsvButtonClick}
          >
            Export as CSV
          </Button>

          <FormControlLabel
            sx={{
              "& .MuiFormControlLabel-label": {
                marginLeft: "12px",
                fontSize: "1.1rem",
                fontWeight: 500,
              },
            }}
            control={
              <Switch
                checked={oldEvents}
                onChange={() => setOldEvents((prev) => !prev)}
                color="primary"
                sx={{
                  transform: "scale(1.5)",
                  marginLeft: 2,
                }}
              />
            }
            label="Show Old Events"
          />

          <FormControlLabel
            sx={{
              "& .MuiFormControlLabel-label": {
                marginLeft: "12px",
                fontSize: "1.1rem",
                fontWeight: 500,
              },
            }}
            control={
              <Switch
                checked={showArchived}
                onChange={() => setShowArchived((prev) => !prev)}
                color="secondary"
                sx={{
                  transform: "scale(1.5)",
                  marginLeft: 2,
                }}
              />
            }
            label="Show Archived Events"
          />
        </Box>

        {!isViewOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              alignSelf: "flex-end",
              mb: 1,
            }}
            disableElevation
            disabled={isLoading || !canCreateEvents}
            onClick={() => navigate("events/create")}
          >
            Create
          </Button>
        )}
        <Menu open={Boolean(menuAnchorElement)} anchorEl={menuAnchorElement} onClose={() => setMenuAnchorElement(null)}>
          {colsDef.map((col: ColDef) => (
            <MenuItem key={col.field}>
              <FormControlLabel control={<Checkbox checked={!col.hide} name={col.field} onChange={handleColumnsMenuChange} />} label={col.headerName} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <div id={styles.eventsGrid} className="ag-theme-quartz" style={{ fontSize: "16px" }}>
        <AgGridReact
          columnDefs={colsDef}
          rowData={events}
          loading={isLoading}
          loadingOverlayComponent={GridLoadingOverlay}
          ref={gridRef}
          pagination
          paginationPageSize={20}
          defaultColDef={{
            headerClass: "tableHeader",
            filter: "agTextColumnFilter",
            filterParams: {
              buttons: ["apply", "clear"],
              closeOnApply: true,
              maxNumConditions: 4,
            },
          }}
          onGridReady={() => gridRef.current?.api.sizeColumnsToFit()}
          onFirstDataRendered={onInitialDataRender}
        />
      </div>
    </Box>
  );
};
